import { useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useFoldResource } from '../../../../hooks/useFoldResource';
import { onboardingFlowAPI } from '../../../../services/api';
import { PageHeader, PageState } from '../outreach/OutreachShared';
import { focusRing } from '../outreach/outreachUtils';
import { readFlowTimeline, readStepFlows, stepFlowsQuery } from './flowAdapter';
import {
  BRANCH_LABELS,
  PLATFORM_OPTIONS,
  VARIANT_OPTIONS,
  eventDetail,
  eventLabel,
  filterSearch,
  flowHref,
  formatCount,
  formatWhen,
  localDay,
  readFilters,
  stepLabel,
} from './flowFunnel';
import { ResourceGate } from './OnboardingFlowPage';

const LISTS = {
  stuck: { title: (label) => `Stuck now on ${label}`, description: "Flows whose latest event is on this step, from 30 minutes to 14 days ago." },
  exited: { title: (label) => `Exited at ${label}`, description: 'Flows that ended on this step without finishing, whether they signed in instead, skipped, or gave up.' },
};

function useFlowSearch() {
  const [searchParams] = useSearchParams();
  const today = localDay();
  return { search: filterSearch(readFilters(searchParams, today), today), today };
}

function BackToFlow({ search }) {
  return (
    <Link
      to={flowHref('/onboarding/flow', search)}
      className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg text-sm font-medium text-blue-700 hover:text-blue-900 ${focusRing}`}
    >
      <ArrowLeftIcon className="h-4 w-4" />
      Onboarding flow
    </Link>
  );
}

function Person({ user, installId }) {
  if (!user) {
    return (
      <>
        <span className="text-gray-700">Signed out</span>
        <span className="block font-mono text-xs text-gray-500">{installId ?? 'No install ID'}</span>
      </>
    );
  }
  return (
    <>
      <span className="font-medium text-gray-900">{user.name ?? user.email ?? user.id}</span>
      {user.name && user.email ? <span className="block text-xs text-gray-600">{user.email}</span> : null}
      {user.organizationName ? <span className="block text-xs text-gray-600">{user.organizationName}</span> : null}
    </>
  );
}

export function FlowStepPage() {
  const { step, kind } = useParams();
  const list = LISTS[kind];
  const { search, today } = useFlowSearch();

  if (!list) {
    return (
      <div className="space-y-4">
        <BackToFlow search={search} />
        <PageState empty emptyTitle="There is no such list" emptyMessage="Open a step's Stuck now or Exited here count from the Onboarding flow page." />
      </div>
    );
  }
  return <StepFlows step={step} kind={kind} list={list} search={search} today={today} />;
}

function StepFlows({ step, kind, list, search, today }) {
  const request = useCallback(
    () =>
      onboardingFlowAPI
        .getStepFlows(step, stepFlowsQuery(readFilters(new URLSearchParams(search), today), kind))
        .then((response) => readStepFlows(response.data)),
    [step, kind, search, today],
  );
  const [resource, reload] = useFoldResource(request, 'These flows could not be loaded.');

  return (
    <div className="space-y-4">
      <BackToFlow search={search} />
      <PageHeader title={list.title(stepLabel(step))} description={list.description} />
      <ResourceGate resource={resource} onRetry={reload} noun="these flows">
        {({ flows, total }) =>
          flows.length === 0 ? (
            <PageState empty emptyTitle="No flows here right now" emptyMessage="Nobody in this range is on this list." />
          ) : (
            <FlowList flows={flows} total={total} search={search} />
          )
        }
      </ResourceGate>
    </div>
  );
}

function FlowList({ flows, total, search }) {
  return (
    <div className="space-y-2">
      {total > flows.length ? (
        <p className="text-sm text-gray-600">
          Showing the newest {formatCount(flows.length)} of {formatCount(total)}.
        </p>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold">Person</th>
              <th scope="col" className="px-4 py-3 font-semibold">Last event</th>
              <th scope="col" className="px-4 py-3 font-semibold">When</th>
              <th scope="col" className="px-4 py-3 font-semibold">
                <span className="sr-only">Timeline</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {flows.map((flow) => (
              <tr key={flow.flowId}>
                <td className="px-4 py-3 align-top">
                  <Person user={flow.user} installId={flow.installId} />
                </td>
                <td className="px-4 py-3 align-top text-gray-800">
                  {flow.lastEvent.event ? eventLabel({ source: 'client', event: flow.lastEvent.event }) : null}
                  {flow.lastEvent.step ? <span className="block text-xs text-gray-600">{stepLabel(flow.lastEvent.step)}</span> : null}
                </td>
                <td className="px-4 py-3 align-top tabular-nums text-gray-800">{formatWhen(flow.lastEvent.at)}</td>
                <td className="px-4 py-3 align-top text-right">
                  <Link
                    to={flowHref(`/onboarding/flow/flows/${encodeURIComponent(flow.flowId)}`, search)}
                    className={`inline-flex min-h-[44px] items-center rounded-lg px-3 font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900 ${focusRing}`}
                  >
                    Open timeline
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const optionLabel = (options, value) => options.find((option) => option.value === value)?.label ?? value;

function Fact({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 break-all text-sm text-gray-900">{children ?? '—'}</dd>
    </div>
  );
}

export function FlowTimelinePage() {
  const { flowId } = useParams();
  const { search } = useFlowSearch();
  const request = useCallback(
    () => onboardingFlowAPI.getFlowTimeline(flowId).then((response) => readFlowTimeline(response.data)),
    [flowId],
  );
  const [resource, reload] = useFoldResource(request, 'This flow could not be loaded.');

  return (
    <div className="space-y-4">
      <BackToFlow search={search} />
      <ResourceGate resource={resource} onRetry={reload} noun="this flow">
        {({ flow, events }) => (
          <>
            <PageHeader
              title={flow.user ? flow.user.name ?? flow.user.email ?? 'Signed-in flow' : 'Signed-out flow'}
              description="Every app and server event for this flow, oldest first. Times are Central."
            />
            <dl className="grid grid-cols-2 gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-4">
              <Fact label="Person">{flow.user ? <Person user={flow.user} installId={flow.installId} /> : 'Signed out'}</Fact>
              <Fact label="Install ID">
                <span className="font-mono text-xs">{flow.installId}</span>
              </Fact>
              <Fact label="Flow ID">
                <span className="font-mono text-xs">{flow.flowId}</span>
              </Fact>
              <Fact label="Path">{BRANCH_LABELS[flow.audience] ?? flow.audience}</Fact>
              <Fact label="Platform">{optionLabel(PLATFORM_OPTIONS, flow.platform)}</Fact>
              <Fact label="App version">{flow.appVersion ? `${flow.appVersion}${flow.build ? ` (${flow.build})` : ''}` : null}</Fact>
              <Fact label="Variant">{optionLabel(VARIANT_OPTIONS, flow.variant)}</Fact>
              <Fact label="Started">{flow.startedAt ? formatWhen(flow.startedAt) : null}</Fact>
            </dl>
            {events.length === 0 ? (
              <PageState empty emptyTitle="No events yet" emptyMessage="This flow has no recorded events." />
            ) : (
              <ol className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white shadow-sm">
                {events.map((event, index) => (
                  <li key={`${event.at}-${index}`} className="grid grid-cols-[9rem_5rem_1fr] gap-3 px-4 py-3 text-sm">
                    <span className="tabular-nums text-gray-600">{formatWhen(event.at)}</span>
                    <span>
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                          event.source === 'server' ? 'border-purple-200 bg-purple-50 text-purple-800' : 'border-blue-200 bg-blue-50 text-blue-800'
                        }`}
                      >
                        {event.source === 'server' ? 'Server' : 'App'}
                      </span>
                    </span>
                    <span>
                      <span className="font-medium text-gray-900"> {eventLabel(event)} </span>
                      {event.step ? <span className="text-gray-600"> on {stepLabel(event.step)}</span> : null}
                      {eventDetail(event) ? <span className="block text-xs text-gray-600">{eventDetail(event)}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </ResourceGate>
    </div>
  );
}
